import { defineComponent as _defineComponent } from 'vue'
import { ModuleContentTitleTrend, PopularityRankTypes, StreamingChartObjectType } from '@/@types/graphql-types';
import WatchNowButtonNew from '@/components/buttons/WatchNowButtonNew.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import SingleRankingDisplay from '@/components/streaming-charts/SingleRankingDisplay.vue';
import StreamingChartsList from '@/components/streaming-charts/StreamingChartsList.vue';
import FilterSelect from '@/components/streaming-charts/ui/FilterSelect.vue';
import StreamingChartContactForm from '@/pages/landing-pages/streaming-charts/StreamingChartContactForm.vue';
import GuideItemCondensed from '@/pages/landing-pages/streaming-charts/GuideItemCondensed.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { WebLocale } from '@/enums/web-locale';
import UseArticleList from '@/features/editorial/composables/useArticleList';
import { addToast } from '@/helpers/vfm/toast-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import BasicButton from '@/ui-components/BasicButton.vue';
import { computed, ref } from 'vue';
import { ShareMethod } from '@/enums/share-methods';

import { faTwitter, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { faArrowRight, faFilm, faGlobeAmericas, faCalendar } from '@fortawesome/free-solid-svg-icons';
import { WEB_LOCALES_EMOJIS } from '@/constants/web-locales.constant';
import {
	reverseCategoryQueryMapping,
	reverseObjectTypeQueryMapping,
} from '@/components/details/streaming-chart/streaming-chart-config';
import { useQuery } from '@/helpers/composables/useApollo';
import {
	GetStreamingChartInfoDocument,
	GetStreamingChartInfoQuery,
	GetStreamingChartInfoQueryVariables,
} from '@/components/graphql/queries/GetStreamingChartInfo.query';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { ClassicUrl, safeOpen } from '@/helpers/clickout-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useRoute, useRouter } from 'vue-router/composables';

type ObjectTypeParam = keyof typeof reverseObjectTypeQueryMapping;
type CategoryParam = keyof typeof reverseCategoryQueryMapping;

type StreamingChartTitle = NonNullable<GetStreamingChartInfoQuery['streamingCharts']['edges']>[number];

export default /*#__PURE__*/_defineComponent({
  __name: 'StreamingCharts',
  setup(__props) {

const languageStore = useLanguageStore();
const routingStore = useRoutingStore();
const { t, tc } = useI18n();

const route = useRoute();
const router = useRouter();

const filterTitleObjectType = ref<StreamingChartObjectType>(
	reverseObjectTypeQueryMapping[route.query.t as ObjectTypeParam] ?? StreamingChartObjectType.Movie
);

const filterCountry = ref<WebLocale | null>(route.query.c === 'global' ? null : languageStore.webLocale);

const filterCategory = ref<PopularityRankTypes>(
	reverseCategoryQueryMapping[route.query.ct as CategoryParam] ?? PopularityRankTypes.WeeklyPopularitySameContentType
);

const queryVariables = computed<GetStreamingChartInfoQueryVariables>(() => {
	let countryStreamingCharts = filterCountry.value?.toUpperCase() ?? null;
	if (countryStreamingCharts === 'UK') countryStreamingCharts = 'GB';

	return {
		language: languageStore.language,
		country: languageStore.country,
		countryStreamingCharts,
		withOffers: true,
		first: 10,
		after: '',
		filter: {
			objectType: filterTitleObjectType.value,
			category: filterCategory.value,
		},
	};
});

const { result, fetchMore, loading, onError } = useQuery<
	GetStreamingChartInfoQuery,
	GetStreamingChartInfoQueryVariables
>(GetStreamingChartInfoDocument, queryVariables, {
	errorPolicy: 'all',
});

onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Streaming Chart Data]:',
		{ error: error, where: 'Component: StreamingChart' },
		'error'
	);
});

const streamingChartData = computed(() => result.value?.streamingCharts);
const streamingChartTitles = computed(() => streamingChartData.value?.edges ?? []);
const firstTitle = computed(() => streamingChartTitles.value?.[0]);

const logoSrc = {
	png: `/${ASSETS_DIR}/img/logo/JustWatch-logo-small.png`,
	webp: `/${ASSETS_DIR}/img/logo/JustWatch-logo-small.webp`,
};

const selectFiltersObjectTypes = computed(() => [
	{ name: tc('WEBAPP_MOVIES'), value: StreamingChartObjectType.Movie },
	{ name: tc('WEBAPP_TVSHOWS'), value: StreamingChartObjectType.Show },
	{ name: tc('WEBAPP_SEASONS'), value: StreamingChartObjectType.ShowSeason },
]);

const allowedCountries = ['us', 'uk', 'de', 'fr', 'es', 'it', 'in', 'mx', 'au', 'br', 'ca', 'tr', 'ar', 'eg', 'pl'];

const selectFiltersCountries = computed(() => {
	const countries = languageStore.locales
		.filter(country => allowedCountries.includes(country.webLocale))
		.map(item => {
			const emoji = WEB_LOCALES_EMOJIS[item.webLocale];
			const name = item.country_names[languageStore.language] ?? item.country;

			return {
				name: emoji ? `${emoji} ${name}` : name,
				value: item.webLocale,
			};
		})
		.toSorted((a, b) => a.name.localeCompare(b.name));

	return [{ name: 'Global', value: null }, ...countries];
});

const selectFiltersCategories = computed(() => [
	{ name: tc('WEBAPP_DAILY'), value: PopularityRankTypes.DailyPopularitySameContentType },
	{ name: tc('WEBAPP_WEEKLY'), value: PopularityRankTypes.WeeklyPopularitySameContentType },
	{ name: tc('WEBAPP_MONTHLY'), value: PopularityRankTypes.MonthlyPopularitySameContentType },
]);

function onLoadMore() {
	// Bot protection
	if (navigator.webdriver) return;

	fetchMore({
		variables: {
			after: streamingChartData?.value?.pageInfo.endCursor,
		},
		updateQuery(previousResult, { fetchMoreResult }) {
			return {
				streamingCharts: {
					__typename: previousResult.streamingCharts.__typename,
					edges: [
						...(previousResult.streamingCharts.edges ?? []),
						...(fetchMoreResult?.streamingCharts.edges ?? []),
					],
					pageInfo: {
						__typename: fetchMoreResult?.streamingCharts.pageInfo.__typename,
						startCursor: fetchMoreResult?.streamingCharts.pageInfo.startCursor ?? '',
						hasPreviousPage: fetchMoreResult?.streamingCharts.pageInfo.hasPreviousPage ?? false,
						hasNextPage: fetchMoreResult?.streamingCharts.pageInfo.hasNextPage ?? false,
						endCursor: fetchMoreResult?.streamingCharts.pageInfo.endCursor ?? '',
					},
					totalCount: fetchMoreResult?.streamingCharts.totalCount ?? 0,
				},
			};
		},
	});
}

function getHeroSpinningText(title: StreamingChartTitle) {
	const titleName =
		title?.node?.__typename === 'Season'
			? `"${title.node.show.content?.title}" (${title.node.content.title})`
			: `"${title.node.content.title}"`;

	if (title?.streamingChartInfo?.trend === ModuleContentTitleTrend.New)
		return t('WEBAPP_STREAMING_CHARTS_DEBUT', { title: titleName });

	if (title?.streamingChartInfo?.trend === ModuleContentTitleTrend.Stable)
		return t('WEBAPP_STREAMING_CHARTS_HOLD', { title: titleName });

	if (
		title?.streamingChartInfo?.trend === ModuleContentTitleTrend.Up &&
		title?.streamingChartInfo?.topRank === 1 &&
		title?.streamingChartInfo.daysInTop3 > 1
	)
		return t('WEBAPP_STREAMING_CHARTS_REGAIN', { title: titleName });

	return `${t('WEBAPP_STREAMING_CHARTS_SPIN', { title: titleName })} ${
		title?.streamingChartInfo?.trendDifference && title.streamingChartInfo?.trendDifference > 0
			? t('WEBAPP_STREAMING_CHARTS_SPIN_RANKCHANGE', {
					rankChange: `+${formatTrendDifference(title.streamingChartInfo?.trendDifference)}`,
			  })
			: ''
	}`;
}

function formatTrendDifference(trendDifference: number) {
	return Intl.NumberFormat(languageStore.language).format(trendDifference);
}

const objectTypeMapping = {
	MOVIE: 'movies',
	SHOW: 'shows',
	SHOW_SEASON: 'seasons',
};

function onObjectTypeChange(value: StreamingChartObjectType) {
	filterTitleObjectType.value = value;
	router.replace({ query: { ...route.query, t: objectTypeMapping[value] } });
}

const categoryMapping = {
	DAILY_POPULARITY_SAME_CONTENT_TYPE: 'daily',
	WEEKLY_POPULARITY_SAME_CONTENT_TYPE: 'weekly',
	MONTHLY_POPULARITY_SAME_CONTENT_TYPE: 'monthly',
};

async function onCategoryChange(value: PopularityRankTypes) {
	filterCategory.value = value;
	router.replace({ query: { ...route.query, ct: categoryMapping[value] } });
}

async function onCountryChange(value: WebLocale | null) {
	filterCountry.value = value;

	if (value == null) {
		router.replace({ query: { ...route.query, c: 'global' } });
		return;
	}

	const localisedStreamingChartsPage = routingStore.urlMetadata?.hrefLangTags.find(
		lt => lt.href.slice(1, 3) === value
	)?.href;

	if (!localisedStreamingChartsPage) return;

	useLanguageStore().setWebLocale(value);
	await router.jw_changeLocale(languageStore.webLocale);
	await router.push({ path: localisedStreamingChartsPage, query: { ...route.query, c: undefined } });
}

function share(title: StreamingChartTitle, method?: ShareMethod) {
	const shareUrl = window.location.href;
	const shareText = getHeroSpinningText(title).toString();

	switch (method) {
		case ShareMethod.FACEBOOK:
			safeOpen(
				new ClassicUrl('http://www.facebook.com/sharer/sharer.php').set('u', shareUrl).set('t', shareText)
			);
			break;
		case ShareMethod.TWITTER:
			safeOpen(new ClassicUrl('https://twitter.com/intent/tweet').set('url', shareUrl).set('text', shareText));
			break;
		default:
			if (navigator && navigator.clipboard) {
				navigator.clipboard.writeText(shareUrl);
				addToast({ message: t('WEBAPP_COPIED_TO_CLIPBOARD') as string });
			}
	}
}

return { __sfc: true,languageStore, routingStore, t, tc, route, router, filterTitleObjectType, filterCountry, filterCategory, queryVariables, result, fetchMore, loading, onError, streamingChartData, streamingChartTitles, firstTitle, logoSrc, selectFiltersObjectTypes, allowedCountries, selectFiltersCountries, selectFiltersCategories, onLoadMore, getHeroSpinningText, formatTrendDifference, objectTypeMapping, onObjectTypeChange, categoryMapping, onCategoryChange, onCountryChange, share, WatchNowButtonNew, PosterGraphql, SingleRankingDisplay, StreamingChartsList, FilterSelect, StreamingChartContactForm, GuideItemCondensed, HiddenHorizontalScrollbar, UseArticleList, BasicButton, ShareMethod, faTwitter, faFacebook, faCopy, faArrowRight, faFilm, faGlobeAmericas, faCalendar }
}

})